import { currentLocation } from "routes/currentLocation";
import { getReduxState } from "state/middleware/errorMonitoring";

interface SpanCollectorLike {
  setAction: (action: string) => SpanCollectorLike;
  setTags: (tags: Record<string, unknown>) => SpanCollectorLike;
}

interface ErrorMonitoringClient {
  sendError: (
    error: unknown,
    callback?: (span: SpanCollectorLike) => void
  ) => void;
}

interface ReduxContext {
  tags: Record<string, unknown>;
  params: Record<string, unknown>;
}

const OMITTED_QUERY_PARAMS = new Set(["code", "token", "invitationToken"]);

const errorMonitoringSearch = (search: string): string => {
  if (!search) return "";

  const searchParams = new URLSearchParams(search);
  OMITTED_QUERY_PARAMS.forEach(param => searchParams.delete(param));

  const safeSearch = searchParams.toString();
  return safeSearch ? `?${safeSearch}` : "";
};

declare global {
  interface Window {
    testSentryError?: (message?: string) => string;
    viewReduxState?: () => unknown;
  }
}

const getReduxContextForError = (): ReduxContext => {
  try {
    const state = getReduxState();

    if (!state) {
      return { tags: {}, params: {} };
    }

    const currentUser = state.currentUser;
    const location = currentLocation();
    const tags: Record<string, unknown> = {
      redux_user_id: String(currentUser?.id || "not_logged_in"),
      redux_user_name: currentUser?.name || "not_logged_in",
      redux_user_email: currentUser?.email || "not_logged_in"
    };

    if (location) {
      tags.redux_current_path = location.pathname;
      tags.redux_current_search = errorMonitoringSearch(location.search || "");
    }

    if (currentUser) {
      tags.redux_user_token_origin = currentUser.tokenOrigin || "";
    }

    return { tags, params: {} };
  } catch (error) {
    console.error("Failed to get Redux context for error", error);
    return { tags: {}, params: {} };
  }
};

const THIRD_PARTY_ERROR_DOMAINS = [/rec\.sessionrewind\.com/];

export const isThirdPartyError = (error: unknown): boolean => {
  const stack = error instanceof Error ? error.stack : undefined;
  if (!stack) return false;

  const frameLines = stack
    .split("\n")
    .filter(line => /^\s+at\s/.test(line) || /https?:\/\//.test(line));

  if (frameLines.length === 0) return false;

  const hasThirdPartyFrame = frameLines.some(line =>
    THIRD_PARTY_ERROR_DOMAINS.some(pattern => pattern.test(line))
  );

  if (!hasThirdPartyFrame) return false;

  const hasAppFrame = frameLines.some(
    line =>
      /\/static\/js\//.test(line) ||
      /\/assets\//.test(line) ||
      /\/src\//.test(line)
  );

  return !hasAppFrame;
};

export const initializeGlobalErrorHandlers = (
  client: ErrorMonitoringClient | null | undefined
): void => {
  if (!client || process.env.NODE_ENV === "test") {
    return;
  }

  window.addEventListener("error", event => {
    if (event.target !== window) {
      return;
    }

    if (event.error) {
      const { tags } = getReduxContextForError();

      client.sendError(event.error, span => {
        span.setAction("window.onerror");
        span.setTags({
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          ...tags
        });
      });
    }
  });

  window.addEventListener("unhandledrejection", event => {
    if (event.reason instanceof Error && isThirdPartyError(event.reason)) {
      return;
    }

    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    const { tags } = getReduxContextForError();

    client.sendError(error, span => {
      span.setAction("unhandledrejection");
      span.setTags({
        type: "promise_rejection",
        reason: String(event.reason),
        ...tags
      });
    });
  });

  console.info("Error monitoring: Global error handlers initialized");
};

export const createTestErrorHelper = (
  client: ErrorMonitoringClient | null | undefined
): void => {
  if (!client) return;

  window.testSentryError = (message = "Test error for Sentry") => {
    try {
      const error = new Error(message);
      const { tags } = getReduxContextForError();

      console.group("📤 Sending test error to Sentry");
      console.log("Message:", message);
      console.log("Tags (metadata):", tags);
      console.log(
        "Redux breadcrumbs are included with reported errors when available."
      );
      console.groupEnd();

      client.sendError(error, span => {
        span.setAction("manual_test_error");
        span.setTags({
          test_error: true,
          triggered_from: "browser_console",
          ...tags
        });
      });

      console.log(
        "✅ Test error sent to Sentry. Check the dashboard in a few minutes."
      );
      return "Error sent successfully";
    } catch (error) {
      console.error("❌ Failed to send test error:", error);
      return "Error sending failed";
    }
  };

  window.viewReduxState = () => {
    try {
      const state = getReduxState();

      if (!state) {
        console.warn(
          "Redux state not available yet. Store may not be initialized."
        );
        return null;
      }

      const currentUser = state.currentUser;

      console.group("📊 Current Redux State");
      console.log("User ID:", currentUser?.id || "not logged in");
      console.log("Current Path:", currentLocation().pathname || "");
      console.log("Current Team:", state.ui?.currentTeam?.id || "");
      console.groupEnd();

      console.group("🔍 Full Redux State");
      console.log(state);
      console.groupEnd();

      return state;
    } catch (error) {
      console.error("Failed to view Redux state:", error);
      return null;
    }
  };

  console.info(
    "Sentry: Test helpers available\n" +
      "  - window.testSentryError('message') - Send test error with breadcrumbs\n" +
      "  - window.viewReduxState() - View current Redux state"
  );
};
